<template>
  <div class="picture-carousel" :class="classes">
    <div class="v-centerer f-w picture-carousel__preview">
      <Icon
        v-if="!noArrows"
        v-quick-title="`${files.length == 0 ? '0' : currentFileIndex + 1} / ${files.length}`"
        class="picture-carousel__prev-arrow"
        @click.native="prevPicture"
      >
        {{ arrowDownLine }}
      </Icon>
      <div
        class="picture-carousel__current-picture h-v-centerer"
        @click="emitPictClick"
      >
        <img
          v-if="isCurrentImage && files.length > currentFileIndex"
          :key="updateModifier"
          :src="currentFileData"
          alt=""
        >
        <div v-else-if="isCurrentImage" class="picture-carousel__placeholder">
          <Icon>{{ cameraPlaceholder }}</Icon>
        </div>
        <embed
          v-else-if="isCurrentPDF && displayPdf"
          ref="media"
          :src="currentFileData"
          width="0px"
          height="0px"
          type="application/pdf"
        />
        <Icon
          v-else
          class="icon"
          :color="iconInfo.color"
        >
          {{ iconInfo.icon }}
        </Icon>
        <div v-if="isCurrentPDF && displayPdf && preventPdfInteraction" class="picture-carousel__click-overlay" />
      </div>
      <Icon
        v-if="!noArrows"
        v-quick-title="`${files.length == 0 ? '0' : currentFileIndex + 1} / ${files.length}`"
        class="picture-carousel__next-arrow"
        @click.native="nextPicture"
      >
        {{ arrowDownLine }}
      </Icon>
    </div>
    <div v-if="!noText" class="picture-carousel__text">
      {{ files.length == 0 ? '0' : currentFileIndex + 1 }} / {{ files.length }}
    </div>
  </div>
</template>

<script>
/**
 * Pictures slider
 * @vue-prop v-model: array of pictures object
 * @vue-prop default-index: number between 0 and files.length -1. The focused piture by default
 * @vue-prop {Boolean} [noArrows] - Remove navigation arrows
 * @vue-prop {Boolean} [noText] - Remove text below the file
 * @vue-prop {Boolean} [displayPdf] - Display PDF files in an 'embed' tag
 * @vue-prop {Boolean} [preventPdfInteraction] - If the displayed file is a PDF, prevent the native browser interaction
 */

import { createNamespacedHelpers } from 'vuex';
// Vuex
import { GET_FILE_DETAILS, FETCH_FILE_DATA } from '@/store/modules/file/action-types';
// Components
import Icon from '@/components/icon.vue';
// Utils
import { RE_IMAGE_TYPE, FILE_TYPE_PDF, getFileIconInfo } from '@/utils/file';
import { arrowDownLine, cameraPlaceholder } from '@/assets/icons';

const { mapActions, mapState } = createNamespacedHelpers('file');

export default {

  name: 'PictureCarousel',

  components: {
    Icon,
  },

  props: {
    value: {
      type: Array,
      default: () => null,
    },
    fileIds: {
      type: Array,
      default: () => null,
    },
    defaultIndex: {
      type: Number,
      default: 0,
    },
    noArrows: Boolean,
    noText: Boolean,
    displayPdf: Boolean,
    preventPdfInteraction: Boolean,
  },

  data() {
    return {
      arrowDownLine,
      cameraPlaceholder,
      currentFileIndex: 0,
      updateModifier: 0,
      // If true when files change, update the index from defaultIndex prop
      doUpdateIndex: true,
    };
  },

  computed: {
    currentFileData() {
      if (this.files.length > this.currentFileIndex) {
        const file = this.files[this.currentFileIndex];
        return this.$store.state.file.fileData[`${file.id}`];
      }
      return '/images/picture-placeholder.png';
    },
    currentFile() {
      if (this.files.length > this.currentFileIndex) {
        return this.files[this.currentFileIndex];
      }
      return null;
    },
    prevSrc() {
      return '/images/prev.png';
    },
    nextSrc() {
      return '/images/next.png';
    },
    /**
     * Files to display in the carousel. Carousel may be used for file-system files usage
     * And classic pictures ({ id, data }) usage
     * @return {Array} - Array of pictures/files
     */
    files() {
      if (this.fileIds) {
        return this.fileIds.map(id => this.fileDetailsData[id] || { id });
      } else {
        return this.value;
      }
    },
    /** Boolean: true if the current focused file is an image */
    isCurrentImage() {
      if (this.fileIds && this.currentFile) {
        return RE_IMAGE_TYPE.test(this.currentFile.type);
      }
      return true;
    },
    isCurrentPDF() {
      if (this.fileIds && this.currentFile) {
        return FILE_TYPE_PDF === this.currentFile.type;
      }
      return false;
    },
    iconInfo() {
      return getFileIconInfo(this.currentFile.type);
    },
    /** Object: classes applied to $el */
    classes() {
      return {
        'no-arrows': this.noArrows,
        'no-text': this.noText,
      };
    },
    ...mapState({
      fileDetailsData: state => state.files,
    }),
  },

  watch: {
    fileIds() {
      this.refresh();
    },
    files() {
      if (this.doUpdateIndex) {
        this.currentFileIndex = this.files.length > this.defaultIndex ? this.defaultIndex : 0;
      }
    },
    defaultIndex() {
      if (this.doUpdateIndex) {
        this.currentFileIndex = this.files.length > this.defaultIndex ? this.defaultIndex : 0;
      }
    },
  },

  created() {
    this.refresh();
    this.currentFileIndex = this.files.length > this.defaultIndex ? this.defaultIndex : 0;
  },

  methods: {
    refresh() {
      if (this.fileIds && this.fileIds.length) {
        this.GET_FILE_DETAILS({ ids: this.fileIds });
        this.fileIds.forEach((fileId) => {
          this.FETCH_FILE_DATA({ fileId });
        });
      }
    },
    emitPictClick() {
      this.doUpdateIndex = false;
      this.$emit('pictureClick', this.currentFileIndex);
    },
    nextPicture() {
      this.currentFileIndex = (this.currentFileIndex + 1) % this.files.length;
      this.emitIndexChange();
    },
    prevPicture() {
      this.currentFileIndex = (this.currentFileIndex - 1 + this.files.length) % this.files.length;
      this.emitIndexChange();
    },
    emitIndexChange() {
      this.doUpdateIndex = false;
      this.$emit('changeIndex', this.currentFileIndex);
    },
    update() {
      this.updateModifier += 1;
    },
    ...mapActions({
      GET_FILE_DETAILS,
      FETCH_FILE_DATA,
    }),
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.picture-carousel {
  display: flex;
  flex-flow: column;

  &__preview {
    flex: 1;
    min-height: 0px;
  }

  &__current-picture {
    flex: 1;
    min-width: 0px;
    margin: 0 0.5rem;
    position: relative;

    .icon {
      width: auto;
      height: 75%;
    }

    embed {
      width: 100%;
      height: 100%;
    }

    img {
      border-radius: $main-border-radius;
    }
  }

  &.no-arrows {
    .picture-carousel__current-picture {
      margin: 0;
    }
  }

  &__next-arrow, &__prev-arrow {
    width: 1rem;
    height: 1rem;
    padding: 0.5rem;
    border-radius: 20px;
    flex: 0 0 auto;
    cursor: pointer;

    &:hover {
      background: $color-gray-background-hover;
    }

    &:active {
      box-shadow: $color-shizen-blue-btn-shadow;
    }
  }

  &__next-arrow {
    transform: rotate(-90deg);
  }

  &__prev-arrow {
    transform: rotate(90deg);
  }

  &__text {
    flex: 0 0 auto;
    text-align: center;
    margin-top: 5px;
    font-weight: bold;
  }

  &__placeholder {
    width: 100%;
    background: $color-gray-background;
    padding: 25%;
    border-radius: $main-border-radius;

    .icon {
      display: block;
      width: 100%;
      height: 100%;
      color: $color-shizen-light-gray;
    }
  }

  &__click-overlay {
    position: absolute;
    top: 0px;
    left: 0px;
    right: 0px;
    bottom: 0px;
  }
}
</style>
